import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePwaResumeReload } from '../hooks/usePwaResumeReload';
import type { UserRole } from '../types';

interface Props {
  /** Role mínima exigida para acessar a rota */
  requiredRole: UserRole;
}

/**
 * ProtectedRoute
 *
 * Avalia a sessão e a role do usuário via JWT claims.
 * - Não autenticado          → redireciona para /login
 * - Role insuficiente        → redireciona para /login
 * - super_admin              → acesso livre a qualquer rota protegida (inclui impersonação tenant)
 * - tenant em /admin-master  → redireciona para /dashboard
 * - tenant suspenso          → tratado por TenantStatusGuard nas rotas tenant
 */
export default function ProtectedRoute({ requiredRole }: Props) {
  const { session, role, loading } = useAuth();

  // Recarrega o PWA admin ao voltar do background (fora de Player/Jukebox).
  usePwaResumeReload();

  if (loading) {
    return (
      <div className="fixed inset-0 bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session || !role) {
    return <Navigate to="/login" replace />;
  }

  if (role === 'super_admin') {
    return <Outlet />;
  }

  if (role === 'tenant' && requiredRole === 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (role !== requiredRole) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}
